import type { CurrencyInfo } from '../store/slices/game/types';

enum SeparatorType {
  Decimal = 'decimal',
  Group = 'group',
}

const numberFormatCache: Record<string, Intl.NumberFormat> = {};

export const formatNumber = (locale: string, value: number): string => {
  let formatter = numberFormatCache[locale];
  if (!formatter) {
    formatter = new Intl.NumberFormat(numberFormatterLocal(locale));
    numberFormatCache[locale] = formatter;
  }
  return formatter.format(value);
};

const exceptionLocales = new Set(['ar', 'fa', 'bn']);

/**
 * Adjusts number formatting for exception locales, defaulting to Western Arabic numeral system.
 * @param {string} locale - The user's local (Example: 'en', 'de', etc.).
 * @return {string} - Adjusted locale for number formatters.
 */
export const numberFormatterLocal = (locale: string): string =>
  exceptionLocales.has(locale) ? 'ar-u-nu-latn' : locale;

export const getCurrencyInfo = (currency: string, locale: string): CurrencyInfo => {
  // This function extracts group and digits number for the currency
  // TODO: Ideally this info should be returned from the server
  // TODO: not sure group group number is needed at all and is same in different locales

  const getSeparator = (locale: string, separatorType: SeparatorType): string => {
    const localeInfo = Intl.NumberFormat(numberFormatterLocal(locale)).formatToParts(10000.1);
    return localeInfo.find((part) => part.type === separatorType)?.value || separatorType === SeparatorType.Decimal
      ? '.'
      : ',';
  };

  // Separators will remain the same as long as same locale is used (en-US)
  const decimalSeparator = '.';
  const groupSeparator = ',';

  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    });
    const testNumber = 10000.123;

    const formattedNumber = formatter.format(testNumber);

    const [firstPart, decimals] = formattedNumber.split(decimalSeparator);
    const [rest, group] = firstPart.split(groupSeparator);

    const numberStart = rest.lastIndexOf('1');
    const symbol = rest.substring(0, numberStart).trim();

    return {
      digitsNumber: decimals?.length || 0,
      groupNumber: group?.length || 0,
      decimalSeparator: getSeparator(locale, SeparatorType.Decimal),
      groupSeparator: getSeparator(locale, SeparatorType.Group),
      symbol,
    };
  } catch (e) {
    // This is an unknown currency, we assume it is EURO like currency
    return {
      symbol: currency,
      digitsNumber: 2,
      decimalSeparator: '.',
      groupSeparator: ',',
      groupNumber: 3,
    };
  }
};
